export const MAX_ENTITY_NAME_LENGTH = 255;
export const MAX_REPORT_DESCRIPTION_LENGTH = 399;
export const MAX_DASHBOARD_NAME_LENGTH = 255;
export const MAX_DASHBOARD_DESCRIPTION_LENGTH = 399;
export const MAX_CONTENT_NAME_LENGTH = 255;
export const MAX_CONTENT_DESCRIPTION_LENGTH = 255;

export const MAX_COHORT_NAME_LENGTH = 190;
export const MAX_COHORT_DESCRIPTION_LENGTH = 750;
export const GRAVATAR_URL = `https://cdn.mxpnl.com/static/asset-cache/5b4ed9e5186a1d3884d45a5c2edebac0/images/engage/default_user_icon.png`;
export const HIDDEN_PEOPLE_PROPERTIES = [
  `$answers`,
  `$campaigns`,
  `$deliveries`,
  `$experiments`,
  `$events_distinct_id`,
  `$mp_message_sends`,
  `$mp_visual_journeys`,
  `$notifications`,
];

// NOTE: This constant is also defined in the webapp code. If you change this constant, you
// should also change the corresponding constant in webapp/app_api/demo_project/util.py.
export const USE_THIS_BOARD_IDENTIFIER = `useBoard`;

// Keep this enum in sync with the corresponding values in analytics/webapp/user/security/twofactor.py.
// Note that because the two sets of values are in two different binaries, you may not be able
// to make changes in a signle PR! Instead, you may have to make a series of backwards-compatible
// changes over several PRs.
export enum TwoFactorOption {
  NOT_SELECTED = `not selected`,
  SMS = `sms`,
  TOTP = `totp`,
}

export const NON_NUMERIC_VALUES_SEGMENT = `$non_numeric_values`;
export const REMAINING_VALUES_SEGMENT = `$remaining_values`;

export enum MPProperties {
  Avatar = `$avatar`,
  AndroidDevices = `$android_devices`,
  Campaigns = `$campaigns`,
  City = `$city`,
  CountryCode = `$country_code`,
  Date = `$date`,
  DistinctId = `$distinct_id`,
  ReplayRetentionPeriod = `$mp_replay_retention_period`,
  GroupId = `$group_id`,
  Email = `$email`,
  Experiments = `$experiments`,
  IosDevices = `$ios_devices`,
  InsertId = `$insert_id`,
  LastSeen = `$last_seen`,
  Region = `$region`,
  $Time = `$time`,
  // the stream endpoint returns "time" instead of the reserved property $time
  Time = `time`,
  MPCountryCode = `mp_country_code`,
  MPProcessingTime = `mp_processing_time_ms`,
  Transactions = `$transactions`,
  Name = `$name`,
  FirstName = `$first_name`,
  LastName = `$last_name`,
  ReferringDomain = `$referring_domain`,
  Unsubscribed = `$unsubscribed`,

  // special property which resolves to $name, but falls back to various
  // other user properties (e.g. $first_name/$last_name) if it is missing
  // this emulates behavior certain reports (like Explore) have had for
  // a long time
  NameWithFallbacks = `$mp_name_with_fallbacks`,

  // special property which resolves to $transactions, but sums the value
  // of all transactions rather than returning the list of objects that make
  // up $transactions, this emulates behavior Explore has had for a long time
  TransactionsSum = `$mp_transaction_sum`,
}

export interface BlockedTimeWindow {
  start: number;
  end: number;
  usage_pct: number;
}

export enum CommonUserProperties {
  Name = `name`,
  FirstName = `first_name`,
  LastName = `last_name`,
}

export enum IconSizeType {
  XS = `xs`,
  Small = `small`,
  Medium = `medium`,
  Large = `large`,
  XL = `xl`,
}

// Keep this enum in sync with the one defined in app_api/avatars/constants.py
export enum AvatarImageSize {
  Small = 48,
  Medium = 64,
  Large = 80,
  XL = 192,
}

// Icon size always half the image resolution
export const ICON_PROPERTIES_BY_SIZE = {
  [IconSizeType.XS]: {
    imgSize: AvatarImageSize.Small,
    iconSize: AvatarImageSize.Small / 2.4,
    fontSize: 12,
  },
  [IconSizeType.Small]: {
    imgSize: AvatarImageSize.Small,
    iconSize: AvatarImageSize.Small / 2,
    fontSize: 12,
  },
  [IconSizeType.Medium]: {
    imgSize: AvatarImageSize.Medium,
    iconSize: AvatarImageSize.Medium / 2,
    fontSize: 14,
  },
  [IconSizeType.Large]: {
    imgSize: AvatarImageSize.Large,
    iconSize: AvatarImageSize.Large / 2,
    fontSize: 14,
  },
  [IconSizeType.XL]: {
    imgSize: AvatarImageSize.XL,
    iconSize: AvatarImageSize.XL / 2,
    fontSize: 24,
  },
};

export const STAFF_DISPLAY_NAME = `Mixpanel Team`;

/**
 * ISO 8601 hour of the day (zero padded)
 *
 * [`00:00`, `01:00`..., `23:00`]
 */
export const HOURS_OF_DAY = [...Array(24).keys()].map((hr) => (hr < 10 ? `0${hr}:00` : `${hr}:00`));

export const DAYS_OF_THE_WEEK = [`Monday`, `Tuesday`, `Wednesday`, `Thursday`, `Friday`, `Saturday`, `Sunday`];

export const SHORT_WINDOW_HEIGHT = 900;
export const MENU_HEIGHT_REDUCTION = 80;

export type Timezone = (typeof TIMEZONES)[number];
export const TIMEZONES = [
  `Africa/Abidjan`,
  `Africa/Accra`,
  `Africa/Addis_Ababa`,
  `Africa/Algiers`,
  `Africa/Asmara`,
  `Africa/Bamako`,
  `Africa/Bangui`,
  `Africa/Banjul`,
  `Africa/Bissau`,
  `Africa/Blantyre`,
  `Africa/Brazzaville`,
  `Africa/Bujumbura`,
  `Africa/Cairo`,
  `Africa/Casablanca`,
  `Africa/Ceuta`,
  `Africa/Conakry`,
  `Africa/Dakar`,
  `Africa/Dar_es_Salaam`,
  `Africa/Djibouti`,
  `Africa/Douala`,
  `Africa/El_Aaiun`,
  `Africa/Freetown`,
  `Africa/Gaborone`,
  `Africa/Harare`,
  `Africa/Johannesburg`,
  `Africa/Kampala`,
  `Africa/Khartoum`,
  `Africa/Kigali`,
  `Africa/Kinshasa`,
  `Africa/Lagos`,
  `Africa/Libreville`,
  `Africa/Lome`,
  `Africa/Luanda`,
  `Africa/Lubumbashi`,
  `Africa/Lusaka`,
  `Africa/Malabo`,
  `Africa/Maputo`,
  `Africa/Maseru`,
  `Africa/Mbabane`,
  `Africa/Mogadishu`,
  `Africa/Monrovia`,
  `Africa/Nairobi`,
  `Africa/Ndjamena`,
  `Africa/Niamey`,
  `Africa/Nouakchott`,
  `Africa/Ouagadougou`,
  `Africa/Porto-Novo`,
  `Africa/Sao_Tome`,
  `Africa/Tripoli`,
  `Africa/Tunis`,
  `Africa/Windhoek`,
  `America/Adak`,
  `America/Anchorage`,
  `America/Anguilla`,
  `America/Antigua`,
  `America/Araguaina`,
  `America/Argentina/Buenos_Aires`,
  `America/Argentina/Catamarca`,
  `America/Argentina/Cordoba`,
  `America/Argentina/Jujuy`,
  `America/Argentina/La_Rioja`,
  `America/Argentina/Mendoza`,
  `America/Argentina/Rio_Gallegos`,
  `America/Argentina/Salta`,
  `America/Argentina/San_Juan`,
  `America/Argentina/San_Luis`,
  `America/Argentina/Tucuman`,
  `America/Argentina/Ushuaia`,
  `America/Aruba`,
  `America/Asuncion`,
  `America/Atikokan`,
  `America/Bahia`,
  `America/Bahia_Banderas`,
  `America/Barbados`,
  `America/Belem`,
  `America/Belize`,
  `America/Blanc-Sablon`,
  `America/Boa_Vista`,
  `America/Bogota`,
  `America/Boise`,
  `America/Cambridge_Bay`,
  `America/Campo_Grande`,
  `America/Cancun`,
  `America/Caracas`,
  `America/Cayenne`,
  `America/Cayman`,
  `America/Chicago`,
  `America/Chihuahua`,
  `America/Costa_Rica`,
  `America/Cuiaba`,
  `America/Curacao`,
  `America/Danmarkshavn`,
  `America/Dawson`,
  `America/Dawson_Creek`,
  `America/Denver`,
  `America/Detroit`,
  `America/Dominica`,
  `America/Edmonton`,
  `America/Eirunepe`,
  `America/El_Salvador`,
  `America/Fortaleza`,
  `America/Glace_Bay`,
  `America/Godthab`,
  `America/Goose_Bay`,
  `America/Grand_Turk`,
  `America/Grenada`,
  `America/Guadeloupe`,
  `America/Guatemala`,
  `America/Guayaquil`,
  `America/Guyana`,
  `America/Halifax`,
  `America/Havana`,
  `America/Hermosillo`,
  `America/Indiana/Indianapolis`,
  `America/Indiana/Knox`,
  `America/Indiana/Marengo`,
  `America/Indiana/Petersburg`,
  `America/Indiana/Tell_City`,
  `America/Indiana/Vevay`,
  `America/Indiana/Vincennes`,
  `America/Indiana/Winamac`,
  `America/Inuvik`,
  `America/Iqaluit`,
  `America/Jamaica`,
  `America/Juneau`,
  `America/Kentucky/Louisville`,
  `America/Kentucky/Monticello`,
  `America/La_Paz`,
  `America/Lima`,
  `America/Los_Angeles`,
  `America/Maceio`,
  `America/Managua`,
  `America/Manaus`,
  `America/Martinique`,
  `America/Matamoros`,
  `America/Mazatlan`,
  `America/Menominee`,
  `America/Merida`,
  `America/Mexico_City`,
  `America/Miquelon`,
  `America/Moncton`,
  `America/Monterrey`,
  `America/Montevideo`,
  `America/Montreal`,
  `America/Montserrat`,
  `America/Nassau`,
  `America/New_York`,
  `America/Nipigon`,
  `America/Nome`,
  `America/Noronha`,
  `America/North_Dakota/Center`,
  `America/North_Dakota/New_Salem`,
  `America/Ojinaga`,
  `America/Panama`,
  `America/Pangnirtung`,
  `America/Paramaribo`,
  `America/Phoenix`,
  `America/Port-au-Prince`,
  `America/Porto_Velho`,
  `America/Port_of_Spain`,
  `America/Puerto_Rico`,
  `America/Rainy_River`,
  `America/Rankin_Inlet`,
  `America/Recife`,
  `America/Regina`,
  `America/Resolute`,
  `America/Rio_Branco`,
  `America/Santarem`,
  `America/Santa_Isabel`,
  `America/Santiago`,
  `America/Santo_Domingo`,
  `America/Sao_Paulo`,
  `America/Scoresbysund`,
  `America/St_Johns`,
  `America/St_Kitts`,
  `America/St_Lucia`,
  `America/St_Thomas`,
  `America/St_Vincent`,
  `America/Swift_Current`,
  `America/Tegucigalpa`,
  `America/Thule`,
  `America/Thunder_Bay`,
  `America/Tijuana`,
  `America/Toronto`,
  `America/Tortola`,
  `America/Vancouver`,
  `America/Whitehorse`,
  `America/Winnipeg`,
  `America/Yakutat`,
  `America/Yellowknife`,
  `Antarctica/Casey`,
  `Antarctica/Davis`,
  `Antarctica/DumontDUrville`,
  `Antarctica/Macquarie`,
  `Antarctica/Mawson`,
  `Antarctica/McMurdo`,
  `Antarctica/Palmer`,
  `Antarctica/Rothera`,
  `Antarctica/Syowa`,
  `Antarctica/Vostok`,
  `Asia/Aden`,
  `Asia/Almaty`,
  `Asia/Amman`,
  `Asia/Anadyr`,
  `Asia/Aqtau`,
  `Asia/Aqtobe`,
  `Asia/Ashgabat`,
  `Asia/Baghdad`,
  `Asia/Bahrain`,
  `Asia/Baku`,
  `Asia/Bangkok`,
  `Asia/Beirut`,
  `Asia/Bishkek`,
  `Asia/Brunei`,
  `Asia/Choibalsan`,
  `Asia/Chongqing`,
  `Asia/Colombo`,
  `Asia/Damascus`,
  `Asia/Dhaka`,
  `Asia/Dili`,
  `Asia/Dubai`,
  `Asia/Dushanbe`,
  `Asia/Gaza`,
  `Asia/Harbin`,
  `Asia/Hong_Kong`,
  `Asia/Hovd`,
  `Asia/Ho_Chi_Minh`,
  `Asia/Irkutsk`,
  `Asia/Jakarta`,
  `Asia/Jayapura`,
  `Asia/Jerusalem`,
  `Asia/Kabul`,
  `Asia/Kamchatka`,
  `Asia/Karachi`,
  `Asia/Kashgar`,
  `Asia/Kathmandu`,
  `Asia/Kolkata`,
  `Asia/Krasnoyarsk`,
  `Asia/Kuala_Lumpur`,
  `Asia/Kuching`,
  `Asia/Kuwait`,
  `Asia/Macau`,
  `Asia/Magadan`,
  `Asia/Makassar`,
  `Asia/Manila`,
  `Asia/Muscat`,
  `Asia/Nicosia`,
  `Asia/Novokuznetsk`,
  `Asia/Novosibirsk`,
  `Asia/Omsk`,
  `Asia/Oral`,
  `Asia/Phnom_Penh`,
  `Asia/Pontianak`,
  `Asia/Pyongyang`,
  `Asia/Qatar`,
  `Asia/Qyzylorda`,
  `Asia/Rangoon`,
  `Asia/Riyadh`,
  `Asia/Sakhalin`,
  `Asia/Samarkand`,
  `Asia/Seoul`,
  `Asia/Shanghai`,
  `Asia/Singapore`,
  `Asia/Taipei`,
  `Asia/Tashkent`,
  `Asia/Tbilisi`,
  `Asia/Tehran`,
  `Asia/Thimphu`,
  `Asia/Tokyo`,
  `Asia/Ulaanbaatar`,
  `Asia/Urumqi`,
  `Asia/Vientiane`,
  `Asia/Vladivostok`,
  `Asia/Yakutsk`,
  `Asia/Yekaterinburg`,
  `Asia/Yerevan`,
  `Atlantic/Azores`,
  `Atlantic/Bermuda`,
  `Atlantic/Canary`,
  `Atlantic/Cape_Verde`,
  `Atlantic/Faroe`,
  `Atlantic/Madeira`,
  `Atlantic/Reykjavik`,
  `Atlantic/South_Georgia`,
  `Atlantic/Stanley`,
  `Atlantic/St_Helena`,
  `Australia/Adelaide`,
  `Australia/Brisbane`,
  `Australia/Broken_Hill`,
  `Australia/Currie`,
  `Australia/Darwin`,
  `Australia/Eucla`,
  `Australia/Hobart`,
  `Australia/Lindeman`,
  `Australia/Lord_Howe`,
  `Australia/Melbourne`,
  `Australia/Perth`,
  `Australia/Sydney`,
  `Canada/Atlantic`,
  `Canada/Central`,
  `Canada/Eastern`,
  `Canada/Mountain`,
  `Canada/Newfoundland`,
  `Canada/Pacific`,
  `Europe/Amsterdam`,
  `Europe/Andorra`,
  `Europe/Athens`,
  `Europe/Belgrade`,
  `Europe/Berlin`,
  `Europe/Brussels`,
  `Europe/Bucharest`,
  `Europe/Budapest`,
  `Europe/Chisinau`,
  `Europe/Copenhagen`,
  `Europe/Dublin`,
  `Europe/Gibraltar`,
  `Europe/Helsinki`,
  `Europe/Istanbul`,
  `Europe/Kaliningrad`,
  `Europe/Kyiv`,
  `Europe/Lisbon`,
  `Europe/London`,
  `Europe/Luxembourg`,
  `Europe/Madrid`,
  `Europe/Malta`,
  `Europe/Minsk`,
  `Europe/Monaco`,
  `Europe/Moscow`,
  `Europe/Oslo`,
  `Europe/Paris`,
  `Europe/Prague`,
  `Europe/Riga`,
  `Europe/Rome`,
  `Europe/Samara`,
  `Europe/Simferopol`,
  `Europe/Sofia`,
  `Europe/Stockholm`,
  `Europe/Tallinn`,
  `Europe/Tirane`,
  `Europe/Uzhgorod`,
  `Europe/Vaduz`,
  `Europe/Vienna`,
  `Europe/Vilnius`,
  `Europe/Volgograd`,
  `Europe/Warsaw`,
  `Europe/Zaporozhye`,
  `Europe/Zurich`,
  `GMT`,
  `Indian/Antananarivo`,
  `Indian/Chagos`,
  `Indian/Christmas`,
  `Indian/Cocos`,
  `Indian/Comoro`,
  `Indian/Kerguelen`,
  `Indian/Mahe`,
  `Indian/Maldives`,
  `Indian/Mauritius`,
  `Indian/Mayotte`,
  `Indian/Reunion`,
  `Pacific/Apia`,
  `Pacific/Auckland`,
  `Pacific/Chatham`,
  `Pacific/Chuuk`,
  `Pacific/Easter`,
  `Pacific/Efate`,
  `Pacific/Enderbury`,
  `Pacific/Fakaofo`,
  `Pacific/Fiji`,
  `Pacific/Funafuti`,
  `Pacific/Galapagos`,
  `Pacific/Gambier`,
  `Pacific/Guadalcanal`,
  `Pacific/Guam`,
  `Pacific/Honolulu`,
  `Pacific/Johnston`,
  `Pacific/Kiritimati`,
  `Pacific/Kosrae`,
  `Pacific/Kwajalein`,
  `Pacific/Majuro`,
  `Pacific/Marquesas`,
  `Pacific/Midway`,
  `Pacific/Nauru`,
  `Pacific/Niue`,
  `Pacific/Norfolk`,
  `Pacific/Noumea`,
  `Pacific/Pago_Pago`,
  `Pacific/Palau`,
  `Pacific/Pitcairn`,
  `Pacific/Pohnpei`,
  `Pacific/Port_Moresby`,
  `Pacific/Rarotonga`,
  `Pacific/Saipan`,
  `Pacific/Tahiti`,
  `Pacific/Tarawa`,
  `Pacific/Tongatapu`,
  `Pacific/Wake`,
  `Pacific/Wallis`,
  `US/Alaska`,
  `US/Arizona`,
  `US/Central`,
  `US/Eastern`,
  `US/Hawaii`,
  `US/Mountain`,
  `US/Pacific`,
  `UTC`,
] as const;

/** Uses a non-breaking space so that the word wraps after the path separator */
export const PATH_SEPARATOR = `\u00a0▸ `;

export const SHOW_MIXPANEL_TIPS_DELAY = 7000;

// Using relative url for download.
export const BENCHMARK_PDF_URL = `/wp-content/uploads/2024/01/2024_Benchmarks_Report.pdf`;

export const AUTOMATED_CUSTOM_PROPERTY_TRACKING = `Automated Custom Property`;
export const CALENDAR_DATE_FORMAT = `YYYY-MM-DD`;
export const MIDNIGHT_TIME_STRING = `12:00 am`;
export const MINUTE_BEFORE_MIDNIGHT_TIME_STRING = `11:59 pm`;
export const SHORT_MONTH_CALENDAR_DATE_FORMAT = `MMM D, YYYY`;
export const SHORT_MONTH_NO_YEAR_CALENDAR_DATE_FORMAT = `MMM D`;
export const NO_YEAR_FORMAT = [`M D`, `MM D`, `MMM D`, `MMM DD`, `MMM Do`, `MMMM D`, `MMMM Do`];
export const TIMESTAMP_FORMAT = `YYYY-MM-DDTHH:mm:ss`;
export const TIMESTAMP_WITH_TIMEZONE_FORMAT = `YYYY-MM-DDTHH:mm:ssZ`;
// Format shown to users after entering time.
export const TIME_12H_LOWER_TRUNCATED_FORMAT = `h:mm a`;

/**
 * The following formats are used to read user time inputs, but should be immediately parsed to an
 * object, date, moment, etc, and should use one of the exported formats above, either the
 * full timestamp with a valid date or the format `h:mm a` used for displaying time to users.
 */
export const USER_INPUT_TIME_FORMATS = [
  `ha`,
  `h a`,
  `h:mma`,
  TIME_12H_LOWER_TRUNCATED_FORMAT,
  `h:mmA`,
  `h:mm A`,
  `h:mm:ssa`,
  `h:mm:ss a`,
  `h:mm:ssA`,
  `h:mm:ss A`,
  `hha`,
  `hh a`,
  `hh:mma`,
  `hh:mm a`,
  `hh:mmA`,
  `hh:mm A`,
  `hh:mm:ssa`,
  `hh:mm:ss a`,
  `hh:mm:ssA`,
  `hh:mm:ss A`,
  `H`,
  `Hmm`,
  `Hmmss`,
  `H:mm`,
  `H:mm:ss`,
  `HH`,
  `HHmm`,
  `HHmmss`,
  `HH:mm`,
  `HH:mm:ss`,
];

export enum SegmentAlignmentStatus {
  Success = `success`,
  FailedRetryable = `failed_retryable`,
  FailedNonRetryable = `failed_non_retryable`,
  NotEligible = `not_eligible`,
  Cancelled = `cancelled`,
}
